import { useEffect } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
  structuredData?: object;
}

const BASE_URL = 'https://klartech-mueritz.de';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  structuredData,
}: SEOConfig) {
  useEffect(() => {
    // Update document title
    const fullTitle = title.includes('Klartech') ? title : `${title} | Klartech Müritz`;
    document.title = fullTitle;

    // Helper to update or create meta tag
    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph tags
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', ogImage, true);
    setMeta('og:url', canonical || window.location.href, true);
    setMeta('og:site_name', 'Klartech Müritz', true);
    setMeta('og:locale', 'de_DE', true);

    // Twitter Card tags
    setMeta('twitter:card', 'summary_large_image', true);
    setMeta('twitter:title', fullTitle, true);
    setMeta('twitter:description', description, true);
    setMeta('twitter:image', ogImage, true);

    // Canonical URL
    let canonicalElement = document.querySelector('link[rel="canonical"]');
    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute('href', canonical || window.location.href);

    // Structured Data
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-structured]');
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-structured', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      const dynamicScript = document.querySelector('script[data-seo-structured]');
      if (dynamicScript) dynamicScript.remove();
    };
  }, [title, description, keywords, canonical, ogType, ogImage, noIndex, structuredData]);
}

// Preset configurations for common pages
export const SEO_PRESETS = {
  home: {
    title: 'IT-Support Waren (Müritz) | Klartech - Computer & Technik-Hilfe für Senioren',
    description: 'Professioneller IT-Support in Waren (Müritz) ✓ Computer-Hilfe für Senioren & Kleinunternehmen ✓ Vor-Ort & Remote-Service ✓ Verständlich ohne Technik-Kauderwelsch',
    keywords: 'IT Support Waren Müritz, Computer Hilfe Senioren, Technik Support Waren, IT Service Müritz, Computer Reparatur Waren',
    canonical: 'https://klartech-mueritz.de/',
  },
  blog: {
    title: 'IT-Blog & Technik-Tipps',
    description: 'Praktische Technik-Tipps und IT-Ratgeber für Senioren und Kleinunternehmen. Verständlich erklärt von Klartech Müritz.',
    keywords: 'IT Blog, Technik Tipps Senioren, Computer Ratgeber, IT Hilfe Artikel',
    canonical: 'https://klartech-mueritz.de/blog',
  },
  appointments: {
    title: 'Termin buchen - IT-Support',
    description: 'Buchen Sie Ihren IT-Support Termin bei Klartech Müritz. Remote-Support ab 8 Uhr, Vor-Ort-Service ab 17:30 Uhr. Schnelle Hilfe bei Computer-Problemen.',
    keywords: 'IT Termin buchen, Computer Support Termin, IT Hilfe Waren Müritz',
    canonical: 'https://klartech-mueritz.de/termin',
  },
  kompakt: {
    title: 'Kompakt - KI-Dokumentenanalyse & Zusammenfassung',
    description: 'Klartech Kompakt - Intelligente KI-gestützte Dokumentenanalyse. Automatische Zusammenfassungen, Sentiment-Analyse und Keyword-Extraktion für Ihre Dokumente.',
    keywords: 'KI Dokumentenanalyse, Dokument Zusammenfassung, Text Analyse KI, PDF Analyse, Sentiment Analyse',
    canonical: 'https://klartech-mueritz.de/kompakt',
    ogType: 'product',
  },
  chat: {
    title: 'KI-Chat Assistent',
    description: 'Ihr digitaler IT-Assistent von Klartech Müritz. Stellen Sie Fragen zu unseren Services oder lassen Sie sich bei IT-Problemen beraten.',
    keywords: 'IT Chat, KI Assistent, Computer Hilfe Chat',
    canonical: 'https://klartech-mueritz.de/chat',
  },
  impressum: {
    title: 'Impressum',
    description: 'Impressum und rechtliche Informationen von Klartech Müritz - IT-Support in Waren (Müritz).',
    canonical: 'https://klartech-mueritz.de/impressum',
    noIndex: false,
  },
  datenschutz: {
    title: 'Datenschutzerklärung',
    description: 'Datenschutzerklärung von Klartech Müritz. Informationen zum Umgang mit Ihren personenbezogenen Daten.',
    canonical: 'https://klartech-mueritz.de/datenschutz',
    noIndex: false,
  },
};
